import { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography
} from "@mui/material";

import { axiosInstance } from "../../utility/apiInstance";
import DetailsTrackingOfAWB from "../AWBDashboard/DetailsTracking";
import { ShieldCloseIcon } from "lucide-react";

const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const TrackingSimulator = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [selectedTrackingId, setSelectedTrackingId] = useState(null);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonDialogTitle, setJsonDialogTitle] = useState("");
    const [jsonDialogContent, setJsonDialogContent] = useState({});


    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);


    useEffect(() => {
        if (debouncedSearchTerm) {
            fetchDataByAWB(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm]);

    const fetchDataByAWB = async (awb) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`api/v1/tracking-simulator/${awb}`);
     
            if (response.data?.success) {
                const mappedRows = response.data.data.map((item, index) => ({
                    sno: index + 1,
                    id: item._id,
                    timestamp: item.time,
                    trackingNumber: item.trackingNumber,
                    intemoJson: item.intemoJson,
                    ourJson: item.ourJson,
                    trackingUrl: item.intemoJson.trackingUrl,
                    destination: item.intemoJson.destination,
                    origin: item.intemoJson.origin,
                    pieces: item.intemoJson.pieces,
                    weight: item.intemoJson.weight,
                    latestpieces: item.intemoJson.events[item.intemoJson.events.length - 1]?.pieces || "",
                    latestweight: item.intemoJson.events[item.intemoJson.events.length - 1]?.weight || "",

                }));
                setRows(mappedRows);
            }
        } catch (error) {
            setRows([]);
        }
        setLoading(false);
    };

    const handleViewJson = (json, title) => {
        setJsonDialogContent(json);
        setJsonDialogTitle(title);
        setJsonDialogOpen(true);
    };
    const columns = [
        {
            field: "sno",
           headerName: "S.No.",
            width: 60,
            renderCell: (params) => {

                return params.value;
            },
        },
        {
            field: "trackingNumber",
            headerName: "trackingNumber",
            width: 110,
            renderCell: (params) => {

                return params.value;
            },
        },
        {
            field: "timestamp",
            headerName: "Timestamp",
            width: 180,
            renderCell: (params) => {
                const rawDate = params.value;
                const date = new Date(rawDate);
                return isNaN(date.getTime()) ? "Invalid" : date.toLocaleString();
            },
        },
        {
            field: "origindetination",
            headerName: "Origin - Destionation",
            width: 150,
            renderCell: (params) => {
                return `${params.row.origin}-${params.row.destination}`
            },
        },
        {
            field: "totalpktsandpackets",
            headerName: "Total Pkts & Weight",
            width: 160,
            renderCell: (params) => {
                return (
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.4" }}>
                        <span>Pkts: {params.row.pieces}</span>
                        <span>Weight: {params.row.weight}</span>
                    </div>
                );
            },
        },
        {
            field: "latestweighttotalpktsandpackets",
            headerName: "Latest update's Pkts & Weight",
            width: 160,
            renderCell: (params) => {
                return (
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.4" }}>
                        <span>Pkts: {params.row.latestpieces}</span>
                        <span>Weight: {params.row.latestweight}</span>
                    </div>
                );
            },
        },
        {
            field: "intemoJson",
            headerName: "Intemo JSON",
            width: 130,
            renderCell: (params) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                        handleViewJson(params.row.intemoJson, `Intemo JSON - ${params.row.intemoJson.awbNumber}`)
                    }
                >
                    View
                </Button>
            ),
        },
        {
            field: "ourJson",
            headerName: "Our JSON",
            width: 130,
            renderCell: (params) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                        handleViewJson(params.row.ourJson, `Our JSON - ${params.row.ourJson.awbNumber}`)
                    }
                >
                    View
                </Button>
            ),
        },

        {
            field: "tracking",
            headerName: "Tracking",
            width: 140,
            renderCell: (params) => (
                <Button
                    endIcon={<OpenInNewIcon />}
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() =>
                        setSelectedTrackingId(params.row)
                    }
                >
                    View
                </Button>
            ),
        }

    ];
    const handleChange = (e) => {
        setSearchTerm(e.target.value);
    };

    
    return (
        <>
            <div className="old_dashboard_title">
                <h1 className="text-2xl font-bold text-gray-700 mt-[-15px] mb-[10px]">
                    Tracking Simulator
                </h1>
            </div>
            <div className="mb-4">
                <TextField
                    id="outlined-basic"
                    label="Search AWB Number"
                    variant="outlined"
                    type="text"
                    value={searchTerm}
                    onChange={handleChange}
                    className="bg-white rounded shadow-sm w-72"
                />
            </div>




            {selectedTrackingId ? (
                <div className="mt-6">
                    <IconButton
                        aria-label="close"
                        onClick={() => setSelectedTrackingId(null)}
                        sx={{
                            position: "absolute",
                            right: 25,
                            top: 150,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <ShieldCloseIcon />
                    </IconButton>
                    <DetailsTrackingOfAWB ourJson={selectedTrackingId.ourJson} trackingId={null} />
                </div>
            ) : (<div className="whiteBox whiteBoxPadding">

                <Box
                    sx={{
                        height: "75vh",
                        width: "100%",
                        "& .actions": {
                            color: "text.secondary",
                        },
                        "& .textPrimary": {
                            color: "text.primary",
                        },
                        "& .new-row-border": {
                            border: "2px solid #e7ebef",
                            textAlign: "center",
                        },
                        boxShadow: "rgba(0, 0, 0, 0.20) 0px 3px 10px",
                    }}
                >
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10]}
                        sx={{
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                textTransform: "uppercase",
                                // fontWeight: "bold",
                            }

                        }}
                    />
                </Box>
            </div>)}
            <Dialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    {jsonDialogTitle}
                    <IconButton
                        aria-label="close"
                        onClick={() => setJsonDialogOpen(false)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <ShieldCloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <pre
                        style={{
                            backgroundColor: "#f5f5f5",
                            padding: "1rem",
                            borderRadius: "4px",
                            maxHeight: "70vh",
                            overflow: "auto",
                            fontSize: "14px",
                        }}
                    >
                        {JSON.stringify(jsonDialogContent, null, 2)}
                    </pre>
                </DialogContent>
            </Dialog>


        </>
    );


};

export default TrackingSimulator;
